import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { PaymentModeBadge, formatDate, money } from "../fuelFormat";

/**
 * Approve or reject one submitted fuel card refill.
 *
 * One modal, two modes - same shape as VehicleRequestDecisionModal: it is the
 * same decision taken two ways over the same row, and the only difference is
 * which field is mandatory (a rejection needs a reason, an approval does not).
 *
 * Approving is what actually loads the money: the server adds the amount to the
 * card's balance at that moment and stamps the before/after figures, so the
 * "balance after approval" shown here is an estimate from the card's balance as
 * last loaded - a purchase recorded in the meantime would shift both.
 */
const FuelRefillDecisionModal = ({ show, mode, refill, onClose, onConfirm, isSaving }) => {
  const isApprove = mode === "approve";
  const [comment, setComment] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (show) {
      setComment("");
      setReason("");
    }
  }, [show, refill?.recordGuid]);

  if (!refill) return null;

  const canConfirm = isApprove ? true : reason.trim().length > 0;
  const balanceNow = Number(refill.cardCurrentBalance || 0);
  const isCash = refill.paymentMode === "CASH"; // Batch200 - no card, no balance move

  const handleConfirm = () =>
    onConfirm(isApprove ? { approvalComment: comment.trim() || null } : { rejectionReason: reason.trim() });

  return (
    <Modal show={show} onHide={isSaving ? undefined : onClose} centered backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isApprove ? "Approve" : "Reject"} {refill.refillNumber}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <dl className="row mb-3" style={{ fontSize: 13 }}>
          {isCash ? (
            <>
              <dt className="col-4 text-muted fw-normal">Payment</dt>
              <dd className="col-8 mb-1"><PaymentModeBadge mode="CASH" /></dd>
              <dt className="col-4 text-muted fw-normal">Vehicle</dt>
              <dd className="col-8 fw-semibold mb-1">
                {refill.regNo} <span className="text-muted fw-normal">({refill.branchCode})</span>
              </dd>
            </>
          ) : (
            <>
              <dt className="col-4 text-muted fw-normal">Fuel card</dt>
              <dd className="col-8 fw-semibold mb-1">
                {refill.cardNumber} <span className="text-muted fw-normal">({refill.provider} · {refill.branchCode})</span>
              </dd>
            </>
          )}
          <dt className="col-4 text-muted fw-normal">Requested by</dt>
          <dd className="col-8 mb-1">{refill.requestedByName || "—"}</dd>
          <dt className="col-4 text-muted fw-normal">Refill date</dt>
          <dd className="col-8 mb-1">{formatDate(refill.refillDate)}</dd>
          <dt className="col-4 text-muted fw-normal">Reference no.</dt>
          <dd className="col-8 mb-1">{refill.referenceNumber || "—"}</dd>
          {refill.remarks && (
            <>
              <dt className="col-4 text-muted fw-normal">Remarks</dt>
              <dd className="col-8 mb-1">{refill.remarks}</dd>
            </>
          )}
          <dt className="col-4 text-muted fw-normal">Refill amount</dt>
          <dd className="col-8 fw-semibold mb-1">{money(refill.refillAmount)}</dd>
          <dt className="col-4 text-muted fw-normal">Supporting document</dt>
          <dd className="col-8 mb-0">{refill.hasAttachment ? refill.attachmentFileName || "attached" : "none attached"}</dd>
        </dl>

        {isApprove ? (
          <>
            {isCash ? (
              <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>
                Cash for <b>{refill.regNo}</b>&apos;s fuel. No card balance changes and nothing is posted to Petty Cash -
                it shows as the vehicle&apos;s Cash line in the Fuel Monthly Summary.
              </div>
            ) : (
              <div className="alert alert-success py-2 mb-3" style={{ fontSize: 13 }}>
                Card balance <b>{money(balanceNow)}</b> → <b>{money(balanceNow + Number(refill.refillAmount || 0))}</b> after
                approval. Approving loads this amount onto the card straight away.
              </div>
            )}
            <div className="mb-0">
              <label className="form-label">Comment</label>
              <textarea
                className="form-control"
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="optional"
              />
            </div>
          </>
        ) : (
          <div className="mb-0">
            <label className="form-label">
              Reason <span className="text-danger">*</span>
            </label>
            <textarea
              className="form-control"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this being rejected? The requester sees this. The card's balance is not changed."
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant={isApprove ? "success" : "danger"} onClick={handleConfirm} disabled={isSaving || !canConfirm}>
          {isSaving ? "Saving…" : isApprove ? "Approve" : "Reject"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FuelRefillDecisionModal;
